/* eslint-disable max-params */
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

// ==================== Text Enhancement API ====================

export type TextEnhancementResponse = {
  original: string
  modified: string
  success: boolean
  error?: string
  tokens_used?: number
  cost_estimate?: number
}

/**
 * MUTATION: Adds or removes emojis from text
 * @param text - Text to enhance
 * @param target - Target for emoji placement (words, sections, lists, or remove)
 * @param fullContent - Optional full content context
 * @param selectionStart - Optional selection start position
 * @param selectionEnd - Optional selection end position
 * @param idToken - Authentication token
 * @param modelId - Optional model ID to use
 * @returns Text enhancement response
 * @category Mutation
 */
export async function addEmojisApi(
  text: string,
  target: "words" | "sections" | "lists" | "remove",
  fullContent?: string,
  selectionStart?: number,
  selectionEnd?: number,
  idToken?: string,
  modelId?: string
): Promise<TextEnhancementResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/text-enhancement/add-emojis`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        text,
        target,
        has_selection: !!text,
        full_content: fullContent,
        selection_start: selectionStart,
        selection_end: selectionEnd,
        model_id: modelId,
      }),
    },
    idToken
  )
  return response.json()
}

/**
 * MUTATION: Polishes/improves text quality
 * @param text - Text to enhance
 * @param fullContent - Optional full content context
 * @param selectionStart - Optional selection start position
 * @param selectionEnd - Optional selection end position
 * @param idToken - Authentication token
 * @param modelId - Optional model ID to use
 * @returns Text enhancement response
 * @category Mutation
 */
export async function polishTextApi(
  text: string,
  fullContent?: string,
  selectionStart?: number,
  selectionEnd?: number,
  idToken?: string,
  modelId?: string
): Promise<TextEnhancementResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/text-enhancement/polish`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        text,
        has_selection: !!text,
        full_content: fullContent,
        selection_start: selectionStart,
        selection_end: selectionEnd,
        model_id: modelId,
      }),
    },
    idToken
  )
  return response.json()
}

/**
 * MUTATION: Adjusts text reading level
 * @param text - Text to enhance
 * @param level - Target reading level
 * @param fullContent - Optional full content context
 * @param selectionStart - Optional selection start position
 * @param selectionEnd - Optional selection end position
 * @param idToken - Authentication token
 * @param modelId - Optional model ID to use
 * @returns Text enhancement response
 * @category Mutation
 */
export async function adjustReadingLevelApi(
  text: string,
  level: "kindergarten" | "elementary" | "middle_school" | "high_school" | "college" | "graduate",
  fullContent?: string,
  selectionStart?: number,
  selectionEnd?: number,
  idToken?: string,
  modelId?: string
): Promise<TextEnhancementResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/text-enhancement/reading-level`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        text,
        level,
        has_selection: !!text,
        full_content: fullContent,
        selection_start: selectionStart,
        selection_end: selectionEnd,
        model_id: modelId,
      }),
    },
    idToken
  )
  return response.json()
}

/**
 * MUTATION: Adjusts text length
 * @param text - Text to enhance
 * @param length - Target length adjustment
 * @param fullContent - Optional full content context
 * @param selectionStart - Optional selection start position
 * @param selectionEnd - Optional selection end position
 * @param idToken - Authentication token
 * @param modelId - Optional model ID to use
 * @returns Text enhancement response
 * @category Mutation
 */
export async function adjustLengthApi(
  text: string,
  length: "much_shorter" | "shorter" | "keep" | "longer" | "much_longer",
  fullContent?: string,
  selectionStart?: number,
  selectionEnd?: number,
  idToken?: string,
  modelId?: string
): Promise<TextEnhancementResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/text-enhancement/adjust-length`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        text,
        length,
        has_selection: !!text,
        full_content: fullContent,
        selection_start: selectionStart,
        selection_end: selectionEnd,
        model_id: modelId,
      }),
    },
    idToken
  )
  return response.json()
}

/**
 * MUTATION: Suggests edits for text
 * @param text - Text to enhance
 * @param fullContent - Optional full content context
 * @param selectionStart - Optional selection start position
 * @param selectionEnd - Optional selection end position
 * @param idToken - Authentication token
 * @param modelId - Optional model ID to use
 * @returns Text enhancement response
 * @category Mutation
 */
export async function suggestEditApi(
  text: string,
  instruction: string,
  fullContent?: string,
  selectionStart?: number,
  selectionEnd?: number,
  idToken?: string,
  modelId?: string
): Promise<TextEnhancementResponse> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/text-enhancement/suggest-edit`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        text,
        instruction,
        has_selection: !!text,
        full_content: fullContent,
        selection_start: selectionStart,
        selection_end: selectionEnd,
        model_id: modelId,
      }),
    },
    idToken
  )
  return response.json()
}
